/**
 * Admin -> Settings -> Integrations: Google akkaunt (Sheets importi, 02-yechim 1-band) va Verifix (HR, 9-band).
 * O'z API'lari bilan darhol saqlaydi; Settings'ning umumiy Save oqimiga aloqasi yo'q.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { cn, dayjs, formatDateTime, formatWhen } from "@/lib/utils";
import { useBranches } from "@/features/admin/api";
import { useUserOptions } from "@/features/drivers/api";
import {
  useUpdateMapping,
  useUpdateVerifix,
  useVerifixAction,
  useVerifixMappings,
  useVerifixRuns,
  useVerifixStatus,
  type Mapping,
  type VerifixAction,
  type VerifixPatch,
  type VerifixStatus,
} from "@/features/integrations/api";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/field";
import { ErrorState, PageLoading } from "@/components/ui/misc";
import { Modal } from "@/components/ui/modal";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { GoogleCard } from "./GoogleCard";
import { Toggle } from "@/components/ui/toggle";
import { SheetsCard } from "./SheetsCard";
import { OcrCard } from "./OcrCard";

export function IntegrationsPanel() {
  const status = useVerifixStatus();
  return (
    <div className="space-y-4">
      <GoogleCard />
      <SheetsCard />
      <OcrCard />
      {status.isPending ? (
        <PageLoading />
      ) : status.isError ? (
        <ErrorState message={status.error.message} onRetry={() => status.refetch()} />
      ) : (
        <>
          <VerifixCard status={status.data} />
          <MappingCard kind="division" status={status.data} />
          <MappingCard kind="employee" status={status.data} />
          <ErrorsCard status={status.data} />
        </>
      )}
    </div>
  );
}

function statusPill(s: VerifixStatus): { key: "error" | "connected" | "disabled" | "notConfigured"; cls: string } {
  if (s.last_error) return { key: "error", cls: "bg-status-rejected text-ink" };
  if (s.configured && s.enabled) return { key: "connected", cls: "bg-status-hired text-ink" };
  if (s.configured) return { key: "disabled", cls: "bg-fill text-ink" };
  return { key: "notConfigured", cls: "bg-fill text-ink" };
}

function VerifixCard({ status }: { status: VerifixStatus }) {
  const { t } = useTranslation("settings");
  const update = useUpdateVerifix();
  const act = useVerifixAction();
  const [filial, setFilial] = useState(status.filial_id);
  const [salaryOpen, setSalaryOpen] = useState(false);
  const [period, setPeriod] = useState(dayjs().subtract(1, "month").format("YYYY-MM"));
  const [pending, setPending] = useState<VerifixAction | null>(null);
  useEffect(() => setFilial(status.filial_id), [status.filial_id]);
  const pill = statusPill(status);

  async function patch(data: Omit<VerifixPatch, "version">) {
    try {
      await update.mutateAsync({ ...data, version: status.version });
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) toast.error(t("integrations.conflict"));
      else toast.error(err instanceof ApiError ? err.message : t("integrations.saveFailed"));
    }
  }

  async function run(action: VerifixAction, body?: Record<string, unknown>) {
    if (!status.configured) {
      toast.error(t("integrations.notConfigured"), t("integrations.notConfiguredDesc"));
      return;
    }
    setPending(action);
    try {
      const res = await act.mutateAsync({ action, body });
      if (res.run.ok) toast.success(t("integrations.done", { kind: res.run.kind_label }), res.run.message || undefined);
      else toast.error(t("integrations.failed", { kind: res.run.kind_label }), res.run.message);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("integrations.requestFailed"));
    } finally {
      setPending(null);
    }
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            Verifix <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold", pill.cls)}>{t(`integrations.pill.${pill.key}`)}</span>
          </span>
        }
        subtitle={t("integrations.subtitle", { filial: status.filial_id || "—", when: formatWhen(status.last_success_at), n: status.employees_count })}
      />
      <CardBody className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("integrations.clientId")} hint={t("integrations.clientIdHint")}>
            <Input value={status.client_id_masked || t("integrations.notSet")} readOnly className="bg-fill/40 text-muted" />
          </Field>
          <Field label={t("integrations.filialId")} hint={t("integrations.filialHint")}>
            <Input value={filial} onChange={(e) => setFilial(e.target.value)} onBlur={() => filial !== status.filial_id && patch({ filial_id: filial })} placeholder="99221" />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => run("test")} loading={pending === "test"}>
            {t("integrations.test")}
          </Button>
          <Button variant="primary" onClick={() => run("sync-employees")} loading={pending === "sync-employees"}>
            {t("integrations.syncNow")}
          </Button>
          <Button onClick={() => setSalaryOpen(true)} loading={pending === "pull-salaries"}>
            {t("integrations.pullSalaries")}
          </Button>
          <Button onClick={() => run("push-bonuses")} loading={pending === "push-bonuses"}>
            {t("integrations.pushBonuses")}
          </Button>
          <div className="ml-auto">
            <Toggle label={t("integrations.enabled")} checked={status.enabled} onChange={(v) => patch({ enabled: v })} />
          </div>
        </div>
        {status.last_error && (
          <div className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
            <b>{t("integrations.lastError")}</b> ({formatDateTime(status.last_error_at)}): {status.last_error}
          </div>
        )}
        <div>
          <div className="mb-2 text-[12px] font-semibold">{t("integrations.whatToSync")}</div>
          <div className="space-y-2">
            <Toggle label={t("integrations.syncEmployees")} hint={t("integrations.hourly")} checked={status.sync_employees} onChange={(v) => patch({ sync_employees: v })} />
            <Toggle label={t("integrations.syncSalaries")} hint={t("integrations.monthlyPro")} checked={status.sync_salaries} onChange={(v) => patch({ sync_salaries: v })} />
            <Toggle label={t("integrations.pushBonusesLabel")} checked={status.push_bonuses} onChange={(v) => patch({ push_bonuses: v })} />
            <Toggle label={t("integrations.syncAttendance")} hint={t("integrations.daily")} checked={status.sync_attendance} onChange={(v) => patch({ sync_attendance: v })} />
          </div>
        </div>
        {status.last_run && (
          <p className="text-[11px] text-muted">
            {t("integrations.lastRun")}: {status.last_run.kind} · {status.last_run.ok ? t("integrations.ok") : t("integrations.runFailed")} · {formatWhen(status.last_run.started_at)}
            {status.last_run.message ? ` · ${status.last_run.message}` : ""}
          </p>
        )}
      </CardBody>
      <Modal
        open={salaryOpen}
        onOpenChange={setSalaryOpen}
        title={t("integrations.pullTitle")}
        description={t("integrations.pullDesc")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setSalaryOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button
              variant="primary"
              onClick={() => {
                setSalaryOpen(false);
                run("pull-salaries", { period });
              }}
            >
              {t("integrations.pull")}
            </Button>
          </>
        }
      >
        <Field label={t("integrations.month")}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {Array.from({ length: 12 }, (_, i) => dayjs().subtract(i, "month").format("YYYY-MM")).map((p) => (
              <option key={p} value={p}>
                {dayjs(p + "-01").format("MMMM YYYY")}
              </option>
            ))}
          </Select>
        </Field>
      </Modal>
    </Card>
  );
}

function MappingCard({ kind, status }: { kind: "division" | "employee"; status: VerifixStatus }) {
  const { t } = useTranslation("settings");
  const rows = useVerifixMappings(kind, status.configured);
  const update = useUpdateMapping();
  const branches = useBranches();
  const users = useUserOptions({}, kind === "employee");
  const [showMatched, setShowMatched] = useState(false);
  const list = rows.data ?? [];
  const unmatched = list.filter((m) => !m.matched);
  const collapsed = list.length > 10 && !showMatched;
  const visible = collapsed ? unmatched : list;

  async function save(m: Mapping, value: string) {
    try {
      if (kind === "division") await update.mutateAsync({ id: m.id, branch: value ? Number(value) : null });
      else await update.mutateAsync({ id: m.id, user: value ? Number(value) : null });
      toast.success(t("integrations.mappingSaved"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("integrations.mappingFailed"));
    }
  }

  return (
    <Card>
      <CardHeader
        title={kind === "division" ? t("integrations.mapDivision") : t("integrations.mapEmployee")}
        subtitle={t("integrations.mapSubtitle", { n: list.length, unmatched: unmatched.length })}
        action={
          list.length > 10 ? (
            <Button size="sm" variant="ghost" onClick={() => setShowMatched((s) => !s)}>
              {showMatched ? t("integrations.hideMatched") : t("integrations.showMatched")}
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        {!status.configured ? (
          <p className="text-[12px] text-muted">{t("integrations.afterSync")}</p>
        ) : visible.length === 0 ? (
          <p className="text-[12px] text-muted">{list.length === 0 ? t("integrations.nothingSynced") : t("integrations.allMatched")}</p>
        ) : (
          <Table variant="bare">
            <thead>
              <tr>
                <Th>{t("integrations.colVerifix")}</Th>
                <Th>{t("integrations.colOurs")}</Th>
                <Th className="w-[260px]">{t("integrations.colMapTo")}</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((m) => (
                <Tr key={m.id}>
                  <Td>
                    {m.external_name || m.external_id} <span className="text-[11px] text-muted">#{m.external_id}</span>
                  </Td>
                  <Td className={cn(!m.matched && "text-danger")}>
                    {m.matched ? (kind === "division" ? m.branch_name : m.user_name) : t("integrations.unmatched")}
                  </Td>
                  <Td>
                    {kind === "division" ? (
                      <Select value={m.branch ?? ""} onChange={(e) => save(m, e.target.value)}>
                        <option value="">{t("integrations.none")}</option>
                        {branches.data?.results.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Select value={m.user ?? ""} onChange={(e) => save(m, e.target.value)}>
                        <option value="">{t("integrations.none")}</option>
                        {users.data?.results.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.full_name} · {u.branch_name ?? ""}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function ErrorsCard({ status }: { status: VerifixStatus }) {
  const { t } = useTranslation("settings");
  const failed = useVerifixRuns({ ok: false }, status.configured);
  const n = failed.data?.count ?? 0;
  return (
    <Card>
      <CardHeader title={t("integrations.errors", { n })} subtitle={t("integrations.errorsDesc")} />
      {n > 0 && (
        <CardBody>
          <ul className="space-y-1 text-[12px]">
            {failed.data?.results.map((r) => (
              <li key={r.id}>
                <span className="font-semibold">{r.kind_label}</span> · {formatDateTime(r.started_at)} · <span className="text-danger">{r.message}</span>
              </li>
            ))}
          </ul>
        </CardBody>
      )}
    </Card>
  );
}
